import { Link } from '@carbon/react';
import { type DataTableColumnDef } from 'components/data-table';
import { formatNumber } from 'utils/number';
import type { InventoryListingRow } from 'types/api-responses';
import styles from './inventory-workspaces-columns.module.scss';

/**
 * The table row IS the response row.
 *
 * The server flattens to the zone grain itself — one line per `(workspace,
 * area, formation, type curve)` — so there is nothing to expand here. The
 * previous client-side flattening of `contextRows` is gone with it.
 */
export type InventoryWorkspaceTableRow = InventoryListingRow &
  Record<string, unknown>;

/**
 * The mock renders every empty cell as a muted em dash.
 * @return {JSX.Element} The placeholder.
 */
const Empty = () => <span className={styles.muted}>—</span>;

const int = (value: number | null | undefined) => {
  if (value === null || value === undefined) return null;
  return formatNumber({ value, minPrecision: 0 });
};

const date = (value: string | null | undefined) => {
  return value ? new Date(value).toLocaleDateString('en-US') : null;
};

/**
 * A workspace-level cell.
 *
 * The rendered v4 mock repeats these on every row of a workspace's group — its
 * source comment claims they print once on the first row, but the running app
 * does not, and blanking them leaves the table looking gap-ridden.
 *
 * @param {React.ReactNode} content - The cell content.
 * @return {React.ReactNode} The content, or a muted em dash when empty.
 */
const groupCell = (content: React.ReactNode) => {
  return content === null || content === undefined || content === '' ? (
    <Empty />
  ) : (
    content
  );
};

/**
 * A cell link, or plain text when the row has no target.
 *
 * Hrefs come off the row rather than being built here: the workspace's own
 * `href`, and `resources.typeCurve` / `resources.typeCurveScenario` for the
 * rest. A null href is normal — a zone with no promoted header has no type
 * curve page to point at — so it renders as text rather than a dead link.
 *
 * @param {object} props - Component props.
 * @param {string | null | undefined} props.href - The target.
 * @param {React.ReactNode} props.children - Link text.
 * @return {React.ReactNode} A link, or the bare text.
 */
const CellLink = ({
  href,
  children,
}: {
  href: string | null | undefined;
  children: React.ReactNode;
}) => {
  if (!href) return <>{children}</>;
  return <Link href={href}>{children}</Link>;
};

/**
 * Columns, in the mock's order and set (inventory-workspaces-list-page/v4).
 *
 * Twenty columns: two workspace identifiers, then the six zone columns, then
 * the rest of the workspace's own fields. The zone columns vary line to line;
 * everything else repeats down a workspace's lines.
 *
 * Deliberately NOT here, because the mock has none of them: Unique TC ID,
 * Inventory Package, Default, Status and Units Verified. The response carries
 * those fields and they can be added if the product asks, but the table is the
 * mock's.
 */
export const inventoryWorkspacesColumns: DataTableColumnDef<InventoryWorkspaceTableRow>[] =
  [
    {
      accessorKey: 'typeCurveScenario',
      id: 'typeCurveScenario',
      header: 'Type Curve Scenario',
      size: 170,
      accessorFn: (row) => row.typeCurveScenario ?? '',
      cell: ({ row }) =>
        groupCell(
          row.original.typeCurveScenario ? (
            <CellLink href={row.original.resources?.typeCurveScenario?.href}>
              {row.original.typeCurveScenario}
            </CellLink>
          ) : null
        ),
    },
    {
      accessorKey: 'name',
      header: 'Workspace',
      size: 220,
      cell: ({ row }) =>
        groupCell(
          <CellLink href={row.original.href}>{row.original.name}</CellLink>
        ),
    },
    // ── Zone-level: varies line to line ──
    {
      accessorKey: 'typeCurveArea',
      header: 'Type Curve Area',
      size: 160,
      meta: { type: 'enum' },
      cell: ({ getValue }) => (getValue() as string | null) ?? <Empty />,
    },
    {
      accessorKey: 'overlapPercent',
      id: 'overlap',
      header: 'Overlap',
      size: 110,
      meta: { align: 'right' },
      // Already a percent and already rounded to two decimals. The mock rounds
      // because its numbers are synthetic; real neighbouring areas overlap by
      // fractions of a percent, and rounding would show most of them as "0%" —
      // reading as no overlap when there is some.
      cell: ({ getValue }) => {
        const value = getValue() as number | null;
        if (value === null || value === undefined) return <Empty />;
        return `${value}%`;
      },
    },
    {
      accessorKey: 'formation',
      id: 'formation',
      header: 'Formation',
      size: 130,
      meta: { type: 'enum' },
      // Code only; the geologic interval is the hover title rather than
      // repeating in the cell.
      cell: ({ row, getValue }) => {
        const code = getValue() as string | null;
        if (!code) return <Empty />;
        return (
          <span title={row.original.formationInterval ?? undefined}>
            {code}
          </span>
        );
      },
    },
    {
      accessorKey: 'typeCurve',
      id: 'typeCurve',
      header: 'Type Curve',
      // The value is a full unique_tc_id — "Midland_CC_proj192_Dean_1320_20250114"
      // is 37 characters — so it needs more room than a name column.
      size: 420,
      cell: ({ row, getValue }) => {
        const name = getValue() as string | null;
        if (!name) return <Empty />;
        return (
          <CellLink href={row.original.resources?.typeCurve?.href}>
            {name}
          </CellLink>
        );
      },
    },
    {
      accessorKey: 'inventoryName',
      id: 'inventory',
      header: 'Inventory',
      size: 180,
      cell: ({ getValue }) => (getValue() as string | null) ?? <Empty />,
    },
    {
      accessorKey: 'ltdProperties',
      id: 'ltdProperties',
      header: 'LTD Properties',
      size: 140,
      meta: { type: 'integer' },
      cell: ({ getValue }) => int(getValue() as number | null) ?? <Empty />,
    },
    // ── Back to workspace level ──
    {
      accessorKey: 'basin',
      header: 'Basin',
      size: 120,
      meta: { type: 'enum' },
      cell: ({ row }) => groupCell(row.original.basin),
    },
    {
      accessorKey: 'state',
      header: 'State',
      size: 100,
      meta: { type: 'enum' },
      cell: ({ row }) => groupCell(row.original.state),
    },
    {
      accessorKey: 'unitCount',
      header: 'Units',
      size: 100,
      meta: { type: 'integer' },
      cell: ({ row }) => groupCell(int(row.original.unitCount)),
    },
    {
      accessorKey: 'propertyCount',
      header: 'Properties',
      size: 120,
      meta: { type: 'integer' },
      // In the mock's set, but the listing contract carries no such field —
      // hidden by default and dashed until the backend has one.
      cell: ({ row }) => groupCell(int(row.original.propertyCount)),
    },
    {
      accessorKey: 'promoted',
      header: 'Promoted',
      size: 110,
      cell: ({ row }) => groupCell(row.original.promoted ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'promotedBy',
      header: 'Promoted By',
      size: 150,
      meta: { type: 'enum' },
      cell: ({ row }) => groupCell(row.original.promotedBy),
    },
    {
      accessorKey: 'verification',
      header: 'Verification',
      size: 140,
      meta: { type: 'enum' },
      cell: ({ row }) => groupCell(row.original.verification),
    },
    {
      accessorKey: 'oilEurMbbl',
      header: 'Oil EUR (Mbbl 30yr)',
      size: 170,
      meta: { type: 'integer' },
      // Mock-only, like Properties — no field in the listing contract.
      cell: ({ row }) => groupCell(int(row.original.oilEurMbbl)),
    },
    {
      accessorKey: 'createdBy',
      header: 'Created By',
      size: 150,
      meta: { type: 'enum' },
      cell: ({ row }) => groupCell(row.original.createdBy),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      size: 140,
      meta: { type: 'datetime' },
      cell: ({ row }) => groupCell(date(row.original.createdAt)),
    },
    {
      accessorKey: 'updatedBy',
      header: 'Updated By',
      size: 150,
      meta: { type: 'enum' },
      cell: ({ row }) => groupCell(row.original.updatedBy),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      size: 140,
      meta: { type: 'datetime' },
      cell: ({ row }) => groupCell(date(row.original.updatedAt)),
    },
  ];

/**
 * Every column starts visible.
 *
 * The mock marks Properties, Oil EUR and Updated By `optional` and starts them
 * off; here all twenty show, and the Columns dialog still switches any of them
 * off. Properties and Oil EUR have no field in the listing contract yet, so
 * they render an em dash until the backend supplies one.
 */
export const INVENTORY_COLUMNS_HIDDEN_BY_DEFAULT = {};
